import React from "react";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "~/components/ui/dropdown-menu";
import { CreditCard, MessageCircleQuestion, LogOut } from "lucide-react";

import Link from "next/link";

import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import { ColorModeSwitch } from "./color-mode-switch";

import {
  useSession,
  useClerk,
  SignedIn,
  SignedOut,
  useAuth,
  useOrganization,
} from "@clerk/nextjs";
import UserButton from "./user-button";
import { roles } from "~/app/(auth-protected-routes)/settings/collaborators/utils";
import { useBrand } from "~/config/brands";
import { getPostSignOutRedirectUrl } from "~/lib/auth-paths";

type UserProfileProps = {
  /** Ghost trigger aligned with marketing header (SiteGlue / public nav). */
  marketingStyle?: boolean;
};

export const UserProfile = ({ marketingStyle = false }: UserProfileProps) => {
  const { session } = useSession();
  const { signOut } = useClerk();
  const auth = useAuth();
  const { organization } = useOrganization();
  const brand = useBrand();

  const handleSignOut = () => {
    void signOut({
      redirectUrl: getPostSignOutRedirectUrl({
        brandId: brand.id,
        organizationName: organization?.name,
        organizationSlug: organization?.slug,
      }),
    });
  };

  return (
    <div className={marketingStyle ? "py-1" : "p-0 sm:p-2"}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={marketingStyle ? "ghost" : "outline"}
            size="sm"
            className={
              marketingStyle
                ? "h-9 w-9 gap-0 p-0 sm:h-auto sm:w-auto sm:gap-2 sm:px-2 sm:py-1.5"
                : "flex h-9 w-9 justify-center p-0 sm:h-auto sm:w-auto sm:justify-start sm:px-3"
            }
          >
            <span
              className={
                marketingStyle
                  ? "rounded-full bg-muted ring-1 ring-border"
                  : "rounded-full bg-red-300 sm:mr-2"
              }
            >
              <UserButton className="h-5 w-5" />
            </span>
            <div className="hidden min-w-0 flex-col justify-start sm:flex">
              <span
                className={
                  marketingStyle
                    ? "text-left text-sm font-medium leading-tight"
                    : "-mb-1 text-left"
                }
              >
                {session?.publicUserData.firstName}
              </span>
              <span
                className={
                  marketingStyle
                    ? "max-w-[10rem] truncate text-[11px] leading-tight text-muted-foreground"
                    : "max-w-[10rem] truncate text-xs text-slate-500"
                }
              >
                {session?.user.primaryEmailAddress?.emailAddress}
              </span>
            </div>
            <span className="sr-only">Toggle user menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center">
          {auth.orgRole && (
            <DropdownMenuLabel
              className="flex justify-center rounded-b bg-muted"
              inset={false}
            >
              <Badge variant="outline" className="w-fit bg-yellow-500">
                {roles.find((r) => r.value === auth.orgRole)?.title ??
                  "Not assigned"}
              </Badge>
            </DropdownMenuLabel>
          )}
          <DropdownMenuSeparator />

          <DropdownMenuItem>
            <CreditCard className="mr-2 h-4 w-4" />
            <Link href="/settings/billing">Billing</Link>
          </DropdownMenuItem>

          <DropdownMenuItem disabled>
            <MessageCircleQuestion className="mr-2 h-4 w-4" />
            Help
          </DropdownMenuItem>
          <DropdownMenuItem>
            <ColorModeSwitch />
          </DropdownMenuItem>
          <DropdownMenuSeparator className="flex" />
          <DropdownMenuItem onClick={handleSignOut}>
            <SignedIn>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </SignedIn>
            <SignedOut>
              <Link href="/sign-in">Sign in</Link>
            </SignedOut>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
